import React from 'react'
import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListSubheader from '@mui/material/ListSubheader'
import Stack from '@mui/system/Stack'

import { Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  formatCurrency,
  useAccountStore,
  useAppConfig,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
} from '../../common'
import type { Account, AccountType, Member } from '../../core'

import { useAccountUiStore } from '../hooks/useUiStore'
import { getClosedAccountBalanceTotal } from '../utils/AccountFields'

import AccountDetailsDrawer from './detailsdrawer/Accounts/AccountDetailsDrawer'
import AccountListItem from './AccountListItem'

const AccountsList = () => {
  const [selectedAccountGuid, setSelectedAccountGuid] = React.useState('')

  const { config } = useAppConfig()
  const { accounts: accountsCopy } = useGlobalCopyStore()
  const { allSelectedAccounts: accounts } = useGlobalUiStore()
  const { setSelectedAccount, setSelectedMember } = useAccountUiStore()
  const { members } = useAccountStore()
  const { onEvent } = useEvent()

  const accountTypes: AccountType[] = [...new Set(accounts.map((account) => account.account_type))]

  const account = accounts.find((account) => selectedAccountGuid === account.guid)

  const hiddenAccounts: Account[] = React.useMemo(
    () => accounts.filter((account) => account.is_hidden),
    [accounts],
  )
  const closedAccounts: Account[] = React.useMemo(
    () => accounts.filter((account) => account.is_closed),
    [accounts],
  )

  const totalHiddenBalance = hiddenAccounts.reduce(
    (total, account) => (account.available_balance || account.balance || 0) + total,
    0,
  )

  const totalClosedBalance = getClosedAccountBalanceTotal(
    closedAccounts,
    accountsCopy,
    config.display_available_balance_in_accounts,
  )

  React.useEffect(() => {
    if (account) {
      setSelectedAccount(account)
      const member =
        members.find((member: Member) => member.guid === account?.member_guid) || ({} as Member)
      if (member) {
        setSelectedMember(member)
      }
    }
  }, [account, selectedAccountGuid])

  const onRowClick = (account: Account) => {
    setSelectedAccountGuid(account.guid)
    onEvent(ANALYTICS_EVENTS.ACCOUNTS_CLICK_ACCOUNT, {
      account_guid: account.guid,
      account_type: account.account_type,
    })
  }

  const getAccountsSection = (sectionType: 'hidden' | 'closed') => {
    const accounts = sectionType === 'hidden' ? hiddenAccounts : closedAccounts
    const totalBalance = sectionType === 'hidden' ? totalHiddenBalance : totalClosedBalance
    const sectionLabel =
      sectionType === 'hidden' ? accountsCopy.account_hidden : accountsCopy.account_closed

    return (
      <Box>
        <List
          aria-label={sectionLabel}
          subheader={
            <ListSubheader component={'h2'} sx={{ backgroundColor: 'transparent', px: 24 }}>
              <Stack
                component="span"
                direction="row"
                sx={{ justifyContent: 'space-between', color: 'grey.700' }}
              >
                <Text bold={true} variant="body1">
                  {sectionLabel}
                </Text>
                <Text bold={true} variant="body1">
                  {formatCurrency(totalBalance, '0,0.00')}
                </Text>
              </Stack>
            </ListSubheader>
          }
        >
          <Stack>
            {accounts.map((account, index, array) => {
              const isLastItem = index === array.length - 1
              return (
                <React.Fragment key={account.guid}>
                  <AccountListItem
                    account={account}
                    onRowClick={() => onRowClick(account)}
                    shouldShowConnectionStatus={true}
                  />
                  {sectionType === 'hidden' ? (
                    <Divider
                      sx={{
                        mb: isLastItem ? 24 : 4,
                        ml: !isLastItem ? 68 : 24,
                      }}
                    />
                  ) : (
                    !isLastItem && (
                      <Divider
                        sx={{
                          mb: 4,
                          ml: 68,
                        }}
                      />
                    )
                  )}
                </React.Fragment>
              )
            })}
          </Stack>
        </List>
      </Box>
    )
  }

  return (
    <Box>
      {accountTypes.map((type) => {
        const accountsOfType = accounts.filter(
          (account) => account.account_type === type && !account.is_closed && !account.is_hidden,
        )
        const accountsOfTypeTotal = accountsOfType.reduce(
          (total, account) => (account.balance || 0) + total,
          0,
        )

        if (accountsOfType.length === 0) {
          return null
        }

        return (
          <List
            aria-label={accountsCopy.account_type_accounts[accountsOfType[0]?.account_type]}
            key={type}
            subheader={
              <ListSubheader component={'h2'} sx={{ backgroundColor: 'transparent', px: 24 }}>
                <Stack
                  component="span"
                  direction="row"
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Text bold={true} variant="body1">
                    {accountsCopy.account_types[type]}
                  </Text>
                  <Text bold={true} variant="body1">
                    {formatCurrency(accountsOfTypeTotal, '0,0.00')}
                  </Text>
                </Stack>
              </ListSubheader>
            }
          >
            <Stack>
              {accountsOfType.map((account) => {
                const isVeryLastItem = accounts.indexOf(account) === accounts.length - 1
                const isLastAccountTypeItem =
                  accountsOfType.indexOf(account) === accountsOfType.length - 1

                return (
                  <React.Fragment key={account.guid}>
                    <AccountListItem
                      account={account}
                      onRowClick={() => onRowClick(account)}
                      shouldShowConnectionStatus={true}
                    />
                    {/* the margin bottom is to counteract Firefly's CSS reset and will ideally be removed at some point */}
                    {(!isVeryLastItem ||
                      closedAccounts.length > 0 ||
                      hiddenAccounts.length > 0) && (
                      <Divider
                        sx={{
                          mb: isLastAccountTypeItem ? 24 : 4,
                          ml: !isLastAccountTypeItem ? 68 : 24,
                        }}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </Stack>
          </List>
        )
      })}
      {hiddenAccounts.length > 0 && getAccountsSection('hidden')}
      {closedAccounts.length > 0 && getAccountsSection('closed')}
      <AccountDetailsDrawer
        isOpen={!!selectedAccountGuid}
        onClose={() => setSelectedAccountGuid('')}
      />
    </Box>
  )
}

export default observer(AccountsList)
